import { env } from '@/utils/env';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  searchParams?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

export interface ApiErrorPayload {
  status: number;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  status: number;

  details?: unknown;

  constructor({ status, message, details }: ApiErrorPayload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

const normalisePath = (path: string): string => {
  const trimmed = path.startsWith('/') ? path.slice(1) : path;
  return trimmed;
};

const joinUrl = (base: string, path: string) => {
  const prefix = base.endsWith('/') ? base.slice(0, -1) : base;
  const suffix = path.startsWith('/') ? path.slice(1) : path;
  return `${prefix}/${suffix}`;
};

export class ApiClient {
  private readonly baseUrl: string;

  private readonly getAccessToken?: () => Promise<string | null>;

  constructor({
    baseUrl = '/api',
    getAccessToken,
  }: {
    baseUrl?: string;
    getAccessToken?: () => Promise<string | null>;
  } = {}) {
    this.baseUrl = baseUrl;
    this.getAccessToken = getAccessToken;
  }

  private async buildHeaders(provided?: Record<string, string>) {
    const headers = new Headers({
      Accept: 'application/json',
      ...provided,
    });

    if (this.getAccessToken) {
      const token = await this.getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    return headers;
  }

  private buildUrl(path: string, searchParams?: RequestOptions['searchParams']) {
    const target = joinUrl(this.baseUrl, normalisePath(path));
    if (!searchParams || Object.keys(searchParams).length === 0) {
      return target;
    }
    const originFallback =
      typeof window === 'undefined'
        ? 'https://placeholder.local'
        : window.location.origin;
    const url = new URL(target, originFallback);
    const params = new URLSearchParams(url.search);
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      params.set(key, String(value));
    });
    url.search = params.toString();
    return url.toString();
  }

  async request<T>(
    method: HttpMethod,
    path: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const { body, headers: providedHeaders, searchParams, signal } = options;
    const needsJson = body !== undefined && method !== 'GET';
    const headers = await this.buildHeaders({
      ...(needsJson ? { 'Content-Type': 'application/json' } : {}),
      ...providedHeaders,
    });

    const response = await fetch(this.buildUrl(path, searchParams), {
      method,
      headers,
      body: needsJson ? JSON.stringify(body) : undefined,
      signal,
    });

    if (!response.ok) {
      let details: unknown;
      try {
        details = await response.json();
      } catch (error) {
        details = await response.text();
      }
      throw new ApiError({
        status: response.status,
        message: response.statusText || 'Request failed',
        details,
      });
    }

    if (response.status === 204) {
      return null as T;
    }

    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return response.json() as Promise<T>;
    }

    return response.text() as unknown as T;
  }

  get<T>(path: string, options?: RequestOptions) {
    return this.request<T>('GET', path, options);
  }

  post<T>(path: string, options?: RequestOptions) {
    return this.request<T>('POST', path, options);
  }

  put<T>(path: string, options?: RequestOptions) {
    return this.request<T>('PUT', path, options);
  }

  delete<T>(path: string, options?: RequestOptions) {
    return this.request<T>('DELETE', path, options);
  }
}

export const createApiClient = (getAccessToken?: () => Promise<string | null>) =>
  new ApiClient({ baseUrl: env.apiBaseUrl, getAccessToken });
